import React, { useState } from 'react'

import Autocomplete, {
	type AutocompleteInputChangeReason,
} from '@mui/material/Autocomplete'
import { useIntl } from 'react-intl'
import { InfiniteData } from '@tanstack/react-query'
import CircularProgress from '@mui/material/CircularProgress'
import type { GetBranchPaginated } from '@matech/thebigpos-sdk'

import { LabelValuePair } from '../types'
import useSearch from '../hooks/useSearch'
import { TextField } from './formControls'
import { DEFAULT_PAGE_SIZE } from '../constants/pagination'
import usePaginatedBranches from '../hooks/usePaginatedBranches'

const DEFAULT_SORT_BY = 'name'
const DEFAULT_ORDER_BY = 'asc'
const DEFAULT_QUERY_STALE_TIME = 60 * 1000 * 5

type BranchesSelectProps = {
	value?: LabelValuePair | null
	onOptionChange?: (option: LabelValuePair | null) => void
	onInputChange?: (
		value: string,
		reason: AutocompleteInputChangeReason
	) => void
	onOptionClose?: () => void
	disabled?: boolean
	error?: boolean
	helperText?: string
	textFieldRef?: React.Ref<HTMLDivElement>
}

const parsePaginatedBranchListToFlat = (
	data: InfiniteData<GetBranchPaginated, number>
) => {
	return data?.pages.flatMap((listPage) =>
		listPage.rows.map(
			(rowItem) =>
				({ value: rowItem.id, label: rowItem.name }) as LabelValuePair
		)
	)
}

// This component will use a abstract Autocomplete from Material in the future
const BranchesSelect = ({
	value,
	error,
	disabled,
	onInputChange,
	onOptionClose,
	onOptionChange,
	textFieldRef,
	helperText,
}: BranchesSelectProps) => {
	const [localInputValue, setLocalInputValue] = useState('')

	const { searchText } = useSearch()
	const { formatMessage } = useIntl()

	const {
		data,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		isLoading: isLoadingUserList,
	} = usePaginatedBranches({
		params: {
			searchText,
		},
		paginationParams: {
			pageSize: DEFAULT_PAGE_SIZE,
			sortBy: DEFAULT_SORT_BY,
			sortDirection: DEFAULT_ORDER_BY,
		},
		staleTime: DEFAULT_QUERY_STALE_TIME,
		select: parsePaginatedBranchListToFlat,
	})

	const handleChangeUser = (
		event: React.SyntheticEvent<Element, Event>,
		value: LabelValuePair | null
	) => onOptionChange?.(value)

	const handleInputChange = (
		event: React.SyntheticEvent<Element, Event>,
		value: string,
		reason: AutocompleteInputChangeReason
	) => {
		onInputChange?.(value, reason)
		setLocalInputValue(value)
	}

	/* 
        ToDo: Remove these inline functions after create a more complete Autocomplete component
        Task Name: [WEB] - Create Autocomplete component
        Task Id: 9696024993
        Task Link: https://mortgage-automation-technologies.monday.com/boards/5842126081/pulses/9696024993
    */
	return (
		<Autocomplete<LabelValuePair>
			options={data || []}
			getOptionLabel={(option) => option.label}
			value={value}
			onChange={handleChangeUser}
			inputValue={localInputValue}
			onInputChange={handleInputChange}
			onClose={onOptionClose}
			isOptionEqualToValue={(option, value) =>
				option.value === value.value
			}
			loading={isLoadingUserList}
			disabled={disabled}
			renderInput={(params) => (
				<TextField
					{...params}
					disabled={disabled}
					inputRef={textFieldRef}
					label={formatMessage({
						id: 'global.select_branch',
					})}
					variant="standard"
					error={error}
					helperText={helperText}
					fullWidth
					required
					InputProps={{
						...params.InputProps,
						endAdornment: (
							<>
								{isLoadingUserList || isFetchingNextPage ? (
									<CircularProgress color="inherit" size={20} />
								) : null}
								{params.InputProps.endAdornment}
							</>
						),
					}}
				/>
			)}
			renderOption={(props, option) => (
				<li
					{...props}
					key={option.value}
					onMouseEnter={() => {
						if (!data) return

						const optionIndex = data?.findIndex(
							(u) => u.value === option.value
						)

						if (
							optionIndex >= data.length - 5 &&
							hasNextPage &&
							!isFetchingNextPage &&
							data.length >= 10
						) {
							fetchNextPage()
						}
					}}
				>
					<div>
						<div className="font-medium">{option.label}</div>
					</div>
				</li>
			)}
			ListboxProps={{
				onScroll: (event) => {
					if (!data) return

					const listboxNode = event.currentTarget
					const isNearBottom =
						listboxNode.scrollTop + listboxNode.clientHeight >=
						listboxNode.scrollHeight / 2

					if (
						isNearBottom &&
						hasNextPage &&
						!isFetchingNextPage &&
						data.length >= DEFAULT_PAGE_SIZE
					) {
						fetchNextPage()
					}
				},
			}}
			noOptionsText={
				!localInputValue
					? formatMessage({
							id: 'share_loan.start_typing_to_search',
						})
					: formatMessage({
							id: 'global.no_branches_found',
						})
			}
		/>
	)
}

export default BranchesSelect
